import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateTaskModal = ({ procedureId, token, open, onClose, onTaskCreated, prefilledData }) => {
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        due_date: '',
        priority: 'medium',
        assigned_to_user_id: ''
    });

    useEffect(() => {
        if (open && prefilledData) {
            setFormData(prev => ({
                ...prev,
                title: prefilledData.title || '',
                description: prefilledData.description || '',
                priority: prefilledData.priority || 'medium',
                // Keep due_date empty as it usually needs manual input even for suggestions
            }));
        } else if (open && !prefilledData) {
            // Reset if opening without data
            setFormData({
                title: '',
                description: '',
                due_date: '',
                priority: 'medium',
                assigned_to_user_id: ''
            });
        }
    }, [open, prefilledData]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.due_date) {
            toast.error('Título y fecha límite son requeridos');
            return;
        }

        setCreating(true);
        try {
            // Convert date to ISO format
            const dueDate = new Date(formData.due_date);
            dueDate.setHours(23, 59, 59, 999); // End of day

            const taskData = {
                title: formData.title,
                description: formData.description || null,
                due_date: dueDate.toISOString(),
                priority: formData.priority,
                assigned_to_user_id: formData.assigned_to_user_id || null
            };

            await axios.post(`${API}/procedures/${procedureId}/tasks`, taskData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Tarea creada exitosamente');

            // Reset form
            setFormData({
                title: '',
                description: '',
                due_date: '',
                priority: 'MEDIUM',
                assigned_to_user_id: ''
            });

            if (onTaskCreated) onTaskCreated();
            onClose();
        } catch (error) {
            console.error('Error creating task:', error);
            toast.error('Error al crear tarea');
        } finally {
            setCreating(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Nueva Tarea</DialogTitle>
                    <DialogDescription>
                        Crea una nueva tarea o acción para este procedimiento
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-4">
                        {/* Title */}
                        <div>
                            <Label htmlFor="title">Título *</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder="Ej: Presentar escrito de contestación"
                                className="mt-1"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <Label htmlFor="description">Descripción</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Detalles adicionales de la tarea..."
                                className="mt-1"
                                rows={3}
                            />
                        </div>

                        {/* Due Date */}
                        <div>
                            <Label htmlFor="due_date">Fecha Límite *</Label>
                            <Input
                                id="due_date"
                                type="date"
                                value={formData.due_date}
                                onChange={(e) => handleChange('due_date', e.target.value)}
                                className="mt-1"
                                required
                            />
                        </div>

                        {/* Priority */}
                        <div>
                            <Label htmlFor="priority">Prioridad</Label>
                            <Select
                                value={formData.priority}
                                onValueChange={(value) => handleChange('priority', value)}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Baja</SelectItem>
                                    <SelectItem value="medium">Media</SelectItem>
                                    <SelectItem value="high">Alta</SelectItem>
                                    <SelectItem value="urgent">Urgente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Assigned User (optional for now) */}
                        <div>
                            <Label htmlFor="assigned_to">Asignar a (opcional)</Label>
                            <Input
                                id="assigned_to"
                                value={formData.assigned_to_user_id}
                                onChange={(e) => handleChange('assigned_to_user_id', e.target.value)}
                                placeholder="ID de usuario"
                                className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Deja vacío para no asignar
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={creating}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={creating}>
                            {creating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creando...
                                </>
                            ) : (
                                'Crear Tarea'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
